import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import {
	FormHeader,
	FormTitle,
	ButtonsContainer,
	Wrapper,
	FormContent
} from '../../../components/ui/form'
import { Button } from '../../../components/ui/button'
import { Download } from '../../../assets/icons'
import { contact, downloadPatientList, getClinicsDetailed, getPatients } from '../../../api'
import { addFlashMessage, hideLoader, hideToast, showLoader, showToast } from '../../../actions'
import to from 'await-catch'
import { connect } from 'react-redux'
import { mapToOptions } from '../../../utils/mapToOptions'
import DatePickerInput from '../../../components/ui/datePicker'
import Autocomplete from '../../../components/ui/autoComplete'
import { MessageText } from '../../../components/ui/datePicker/styled'
import { Section } from '../../../styled/sections'
import ResultsListDesk from '../../../components/resultsListDesk'
import ResultsList from '../../../components/resultCard'
import Pagination from '../../../components/pagination'
import useIsMobile from '../../../hooks/useIsMobile'
import { CLINICS_ID } from '../../../constants'
import moment from 'moment'

const fields = [
	{ label: 'N° Afiliado', key: 'numAfiliado' },
	{ label: 'Paciente', key: 'nombre' },
	{ label: 'Ingreso', key: 'fechaIngreso' },
	{ label: 'Egreso', key: 'fechaEgreso' },
	{ label: 'Clínica', key: 'clinica' },
	{ label: 'Área', key: 'area' },
	{ label: 'Habitación', key: 'habitacion' },
	{ label: 'Cama', key: 'cama' },
	{ label: 'Condición de Ingreso', key: 'condicionIngreso' },
	{ label: 'Diagnóstico de Ingreso', key: 'diagnosticoIngreso' },
]

const HospitalizedPatients = ({ auth, patient, showToast }) => {
	const [clinicsOptions, setClinicOptions] = useState([])
	const [dataList, setDataList] = useState(undefined)
	const [pagination, setPagination] = useState({ totalPaginas: 1, pagina: 1, itemsPorPagina: 10 })
	const [sort, setSort] = useState({ filtro: '', orden: '' })
	const [lastSubmittedFilters, setLastSubmittedFilters] = useState(null)
	const isMobile = useIsMobile()
	const fieldWidth = isMobile ? '100%' : '344px'
	const [savedSearch, setSavedSearch] = useState({})

	const formatDate = value => (value ? moment(value).format('DD/MM/YYYY') : undefined)

	const fetchPatients = async ({ values, page = 1, sortOverride = sort }) => {
		try {
			showLoader()

			const dataToSubmit = {
				...values,
				fechaDesde: formatDate(values.fechaDesde),
				fechaHasta: formatDate(values.fechaHasta),
				pagina: page,
				itemsPorPagina: pagination.itemsPorPagina,
				filtro: sortOverride.filtro,
				orden: sortOverride.orden,
			}

			const [err, { data } = {}] = await to(getPatients(dataToSubmit))
			if (err) throw new Error('Error en el fetch de pacientes')

			setDataList(data?.items || [])
			setPagination(prev => ({ ...prev, pagina: page, totalPaginas: data?.totalPaginas || 1 }))
			setSavedSearch(values)
		} catch (error) {
			addFlashMessage('Ocurrió un error cargando los pacientes')
		} finally {
			hideLoader()
		}
	}

	const handlePageChange = page => {
		if (!lastSubmittedFilters) {
			setPagination(prev => ({ ...prev, pagina: page }))
			return
		}

		fetchPatients({ values: lastSubmittedFilters, page, sortOverride: sort })
	}

	useEffect(() => {
		const fetchData = async () => {
			try {
				showLoader()
				const [err, [{ data: clinicsData } = {}]] = await to([getClinicsDetailed()])
				if (err) throw new Error('Error en el fetch de clínicas')
				const clinicsFiltered = clinicsData.filter(clinic =>
					clinic.id !== CLINICS_ID.CentroBazte && clinic.id !== CLINICS_ID.CentroRita);
				setClinicOptions(mapToOptions(clinicsFiltered, 'id', 'nombre'))
			} catch (error) {
				addFlashMessage('Ocurrió un error cargando las clínicas')
			} finally {
				hideLoader()
			}
		}

		fetchData()
	}, [])

	const handleFormSubmit = async (values) => {
		setLastSubmittedFilters(values)
		fetchPatients({ values, page: 1, sortOverride: sort })
	}

	const handleSort = (filtro, orden) => {
		const nextSort = { filtro, orden }
		setSort(nextSort)

		if (!lastSubmittedFilters) return

		fetchPatients({ values: lastSubmittedFilters, page: 1, sortOverride: nextSort })
	}

	const download = async () => {
		const dataToSubmit = {
			...savedSearch,
			desde: moment(savedSearch.fechaDesde).format('DD/MM/YYYY'),
			hasta: moment(savedSearch.fechaHasta).format('DD/MM/YYYY'),
		}
		
		const [err, { data } = {}] = await to(downloadPatientList(dataToSubmit))
		if (!err) {
			const blob = new Blob([data])
			const filename = `listado_paciente_${new Date().getTime()}.xls`

			if (window.navigator && window.navigator.msSaveOrOpenBlob) {
				window.navigator.msSaveBlob(blob, filename)
			} else {
				var link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = filename
				link.click()
			}

			showToast({
				message: 'El excel fue exportado con éxito',
				type: 'success',
			})
		} else {
			showToast({
				message: 'Ocurrió un error al exportar el excel',
				type: 'error',
			})
		}
	}

	return (
		<>
			<Section>
				<Formik
					initialValues={{
						fechaDesde: null,
						fechaHasta: null,
						clinica: null,
						cuit: auth.metadatos.find(metadata => metadata.metadato.clave === 'CUIT').valor,
					}}
					onSubmit={handleFormSubmit}
					validate={values => {
						const errors = {}

						if (!values.fechaDesde) {
							errors.fechaDesde = 'La fecha desde es obligatoria'
						}

						if (!values.fechaHasta) {
							errors.fechaHasta = 'La fecha hasta es obligatoria'
						}

						if (!values.clinica) {
							errors.clinica = 'La clínica es obligatoria'
						}

						return errors
					}}
				>
					{({ values, handleSubmit, setFieldValue, isValid }) => {

						return (
							<Wrapper>
								<FormHeader align="start">
									<FormTitle align={'start'} font={"24"}>Pacientes internados</FormTitle>
								</FormHeader>
								<FormContent
									as="form"
									style={{
										alignItems: isMobile ? 'stretch' : 'flex-end',
										flexDirection: isMobile ? 'column' : 'row',
										gap: '16px',
										flexWrap: isMobile ? 'nowrap' : 'wrap',
										marginTop: isMobile ? '24px' : '32px'
									}}
								>
									<DatePickerInput
										mode='range'
										startLabel='Fecha desde'
										endLabel='Fecha hasta'
										width={fieldWidth}
										fullWidth={isMobile}
										stackOnMobile={false}
										startPlaceholder='Seleccioná'
										endPlaceholder='Seleccioná'
										startDate={values.fechaDesde}
										endDate={values.fechaHasta}
										onChange={({ startDate, endDate }) => {
											setFieldValue('fechaDesde', startDate)
											setFieldValue('fechaHasta', endDate)
										}}
										requireCompleteRange
									/>
									<Autocomplete
										label={'Instituciones'}
										name="clinica"
										value={values.clinica}
										onChange={e => setFieldValue('clinica', e?.target?.value ?? e)}
										options={clinicsOptions}
										placeholder="Seleccioná"
										width={fieldWidth}
										fullWidth={isMobile}
									/>
									<ButtonsContainer
										width={isMobile ? '100%' : 'auto'}
										direction={isMobile ? 'column' : 'row'}
										justify="start"
										gap={12}
										align={isMobile ? 'stretch' : 'end'}
									>
										<Button
											label={'Consultar'}
											$variant="primary"
											size="large"
											width={isMobile ? '100%' : '200px'}
											type="submit"
											onClick={handleSubmit}
											disabled={!isValid}
										/>
										<Button
											label={'Exportar excel'}
											$variant="secondary"
											size="large"
											width={isMobile ? '100%' : '190px'}
											iconLeft={<Download useCurrentColor />}
											type="button"
											onClick={() => download(values)}
											disabled={!dataList?.length}
										/>
									</ButtonsContainer>
								</FormContent>
							</Wrapper>
						)
					}}
				</Formik>
			</Section>

			{dataList &&
				(<>
					<Section>
						{isMobile ? (
							<ResultsList
								fields={fields}
								results={dataList}
								allTitlesBold
							/>
						) : (
							<ResultsListDesk isSearch={true} results={dataList} handleSort={handleSort} />
						)}
					</Section>
					<Pagination
						totalPages={pagination.totalPaginas}
						currentPage={pagination.pagina}
						onPageChange={handlePageChange}
					/>
				</>)}
		</>
	)
}

const mapStateToProps = ({ auth }) => {
	return { auth }
}

const mapDispatchToProps = {
	showToast,
	hideToast
}

export default connect(mapStateToProps, mapDispatchToProps)(HospitalizedPatients)